import moment from 'moment'
import { InsuredObjectGroup } from '../../../@types/CreateProposal'
import { Address, MatchedEntity } from '../../../@types/GetEntitiesDetails'
import {
  EntityData,
  PackCampaign,
  RunPetisSimulationResponse,
} from '../../../@types/RunPetisSimulation'
import { SimulationSummaryDownloadClientResponse } from '../../../@types/SimulationSummaryDownload'
import { Report } from '../../../Components/SimulationReport/Identification'
import { TypeOfPriceInstallment } from '../../../Stores/simulatorStore'
import { AnimalsSecured } from '../../../Types/AnimalStore'
import { EntityPayload } from '../../../Types/EntityDetails'
import { MatchedIndividualEntityDetailsForSummaryDocument } from '../../../Types/getEntityDetailsForSummaryDocument'
import { ID_EXTERNAL_TYPES } from '../../../constants/idExternalTypes'

export const mountedInsuredObjectGroup = (
  animalList: AnimalsSecured[],
  runPetisSimulation: RunPetisSimulationResponse,
  typeOfPrice: TypeOfPriceInstallment,
  selectedPackCampaign?: PackCampaign,
) => {
  const insuredObjectGroupId =
    runPetisSimulation?.insuredObjectGroup?.insuredObjectGroupId

  const packPremium = (animal: AnimalsSecured) => {
    const currentPremium = animal?.pack?.premiums?.find(
      (item) => item.installment === typeOfPrice,
    )

    return {
      insuredObjectCommercialPremiumValue:
        Number(
          currentPremium?.premiumsValues?.insuredObjectCommercialPremiumValue,
        ) || 0,
      insuredObjectTotalPremiumValue:
        Number(
          currentPremium?.premiumsValues?.insuredObjectTotalPremiumValue,
        ) || 0,
      issueCost: currentPremium?.issueCost || 0,
      premiumToDisplayed:
        Number(
          currentPremium?.premiumsValues?.insuredObjectTotalPremiumValue,
        ) || 0,
      subsequentInstallmentsValue:
        currentPremium?.subsequentsInstallmentValue || 0,
      annual: currentPremium?.annual || 0,
      invoiceRefPremium: currentPremium?.invoiceRefPremium || 0,
      insuredObjectTotalPremiumWithoutIssueCost:
        currentPremium?.premiumsValues
          ?.insuredObjectTotalPremiumWithoutIssueCost || 0,
      insuredObjectFirstInstallmentTotalPremium:
        currentPremium?.premiumsValues
          ?.insuredObjectFirstInstallmentTotalPremium || 0,
      insuredObjectSubsequentInstallmentsTotalPremium:
        currentPremium?.premiumsValues
          ?.insuredObjectSubsequentInstallmentsTotalPremium || 0,
    }
  }

  const selectedPack = (insuredObjectId: string) => {
    const animal = animalList.find((animal) => animal.id === insuredObjectId)
    return {
      id: animal?.pack?.packId || '',
      description: animal?.pack?.packDescription || '',
      order: '0',
      premium: packPremium(animal!),
    }
  }

  const insuredObjectGroup: InsuredObjectGroup = {
    id: insuredObjectGroupId || '',
    code: runPetisSimulation?.insuredObjectGroup?.insuredObjectGroupCode || '',
    sequence:
      runPetisSimulation?.insuredObjectGroup?.insuredObjectGroupSequence || 0,
    insuredObjects:
      runPetisSimulation?.insuredObjectGroup?.insuredObjects?.map((item) => ({
        id: item?.insuredObjectId || '',
        code: item?.insuredObjectCode || '',
        type: item?.insuredObjectType || '',
        sequence: item?.insuredObjectSequence || 0,
        questionnaire: {
          startDate: item?.questionnaire?.startDate || '',
          endDate: item?.questionnaire?.endDate || '',
          questionnareCode: item?.questionnaire?.id || '',
          statusCode: item?.questionnaire?.statusCode || '',
          sections:
            item?.questionnaire?.sections?.map((section) => ({
              name: section?.title || '',
              code: section?.code || '',
              questions:
                section?.questions?.map((question) => ({
                  code: question?.id || '',
                  name: question?.id || '',
                  order: question?.order || 0,
                  description: question.questionPhrase || '',
                  answers:
                    question?.answers?.map((answer) => ({
                      code: answer?.id || '',
                      description: answer?.answerPhrase || '',
                      answerDate: answer?.date || '',
                      responseTypeCode: answer?.responseTypeCode || '',
                    })) || [],
                })) || [],
            })) || [],
        },
        insuredPet: {
          id: item?.insuredObjectId || '',
          name: item?.insuredPet?.animalName || '',
          breedCode: item?.insuredPet?.animalBreedCode || '',
          birthDate: item?.insuredPet?.animalBirthDate || '', // Current Format YYYY-MM-DD
          speciesCode: item?.insuredPet?.animalSpeciesCode || '',
          type: item.insuredPet?.type || '',
        },
        selectedPack: selectedPack(item?.insuredObjectId!),
      })) || [],
  }

  return insuredObjectGroup
}

export const mountedEntityData = (
  entity: EntityPayload,
  entitySelected: MatchedEntity | null,
  endDate: string,
  entitySelectedAddress: Address | null,
  entityDetailsForSummaryDocumentSelected: MatchedIndividualEntityDetailsForSummaryDocument | null,
  runPetisPolicyholder: EntityData,
  simulationSummary: SimulationSummaryDownloadClientResponse | null,
  report: Partial<Report>,
) => {
  const isGlobalEntities =
    (simulationSummary?.idExternalType ||
      runPetisPolicyholder?.idExternalType) === ID_EXTERNAL_TYPES.globalEntity

  return {
    address: {
      postalCode:
        entitySelectedAddress?.postalCode || entity?.address?.postalCode || '',
      addressPointer:
        entitySelectedAddress?.sequence ||
        entitySelected?.addresses?.[0].sequence ||
        '',
      locality: '', // TODO: Não temos esse valor
      address: '', // TODO: Não temos esse valor
      streetType: '', // TODO: Não temos esse valor
      doorNumber:
        entitySelected?.addresses?.[0]?.typology?.postalAddress?.doorNumber ||
        '',
      floor:
        entitySelected?.addresses?.[0]?.typology?.postalAddress?.floorNumber ||
        '',
      fraction: '', // TODO: Não temos esse valor
    },
    contactDetails: {
      phonePointer: entity?.contactDetails?.phonePointer || '',
      mailingAddressPointer:
        entity?.contactDetails?.mailingAddressPointer || '',
      phoneContact: report.phone || '',
      emailAddress: report.email || '',
    },
    idExternalType:
      simulationSummary?.idExternalType ||
      runPetisPolicyholder?.idExternalType ||
      '',
    entityId:
      entitySelected?.idEntity ||
      entityDetailsForSummaryDocumentSelected?.idEntity ||
      simulationSummary?.entityId ||
      '',
    name: entity?.name || report.name || '',
    birthDate:
      moment(entitySelected?.birthdate || '1977-07-26').format() || null,
    initialDate: entity?.initialDate || null, // Current Format YYYY-MM-DD
    endDate: entity?.endDate || endDate,
    idExternal: entitySelected?.idEntity || '',
    role: entity?.role || '',
    payer: isGlobalEntities,
    fiscalNumber: entitySelected?.vatNumber || report.vatNumber || '',
    eni: false, // TODO: Não temos esse valor
    entityType: entitySelected?.entityType || '',
    nationality: '', // TODO: Não temos esse valor
    gender: '', // TODO: Não temos esse valor
    job: '', // TODO: Não temos esse valor
    countryCode: '', // TODO: Não temos esse valor
    agentCode: '', // TODO: Não temos esse valor
    cae: entitySelected?.caes?.[0]?.cae || '', // TODO: Não temos esse valor
  }
}
